

import { type ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, MoreHorizontal } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Checkbox } from "@/shared/ui/checkbox";
import { Badge } from "@/shared/ui/badge";

/**
 * 汎用的なカラム設定
 */
export interface GenericColumnConfig<T> {
  /** カラムのアクセサキー */
  accessorKey: keyof T;
  /** ヘッダーテキスト */
  header: string;
  /** ソート可能かどうか */
  sortable?: boolean;
  /** カラム幅 */
  size?: number;
  /** セルの内容をカスタマイズする関数 */
  cell?: (value: any, row: T) => React.ReactNode;
  /** ヘッダーのアライメント */
  headerAlign?: 'left' | 'center' | 'right';
  /** セルのアライメント */
  cellAlign?: 'left' | 'center' | 'right';
}

/**
 * 汎用的なアクションメニュー設定
 */
export interface ActionMenuConfig<T> {
  /** アクションメニューのアイテム */
  items: Array<{
    label: string;
    onClick: (row: T) => void;
    variant?: 'default' | 'destructive';
    show?: (row: T) => boolean;
  }>;
  /** デフォルトのIDコピー機能を表示するか */
  showCopyId?: boolean;
}

/**
 * 選択可能なチェックボックスカラムを生成
 */
export function createSelectColumn<T>(): ColumnDef<T> {
  return {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="全て選択"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="行を選択"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 50,
  };
}

/**
 * ソート可能なヘッダーボタンを生成
 */
export function createSortableHeader(text: string, align: 'left' | 'center' | 'right' = 'left') {
  const SortableHeaderComponent = ({ column }: any) => {
    const alignClass = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end'
    }[align];

    return (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className={`h-auto p-0 font-semibold hover:bg-transparent ${alignClass} w-full`}
      >
        {text}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    );
  };

  SortableHeaderComponent.displayName = 'SortableHeaderComponent';
  return SortableHeaderComponent;
}

/**
 * 通常のヘッダーテキストを生成
 */
export function createHeader(text: string, align: 'left' | 'center' | 'right' = 'left') {
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  }[align];

  return <div className={`font-semibold ${alignClass}`}>{text}</div>;
}

/**
 * 汎用的なデータカラムを生成
 */
export function createDataColumn<T>(config: GenericColumnConfig<T>): ColumnDef<T> {
  const cellAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  }[config.cellAlign || 'left'];

  return {
    accessorKey: config.accessorKey as string,
    header: config.sortable
      ? createSortableHeader(config.header, config.headerAlign) as any
      : createHeader(config.header, config.headerAlign),
    cell: ({ row }) => {
      const value = row.getValue(config.accessorKey as string);
      const content = config.cell ? config.cell(value, row.original) : (value as React.ReactNode);
      return <div className={cellAlignClass}>{content}</div>;
    },
    size: config.size,
  };
}

/**
 * 通貨フォーマット用のセル
 */
export function createCurrencyCell(value: any): React.ReactNode {
  if (value == null || value === '') return '-';
  const amount = parseFloat(value);
  const formatted = new Intl.NumberFormat("ja-JP").format(amount);
  return <div className="text-right font-medium">{formatted}</div>;
}

/**
 * 日付フォーマット用のセル
 */
export function createDateCell(value: any): React.ReactNode {
  if (!value) return '-';
  const date = new Date(value);
  return (
    <div className="text-sm text-muted-foreground">
      {date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })}
    </div>
  );
}

/**
 * ブール値用のバッジセル
 */
export function createBooleanBadgeCell(
  trueLabel: string,
  falseLabel: string,
  trueVariant: 'default' | 'secondary' | 'destructive' | 'outline' = 'default',
  falseVariant: 'default' | 'secondary' | 'destructive' | 'outline' = 'secondary'
) {
  const BooleanBadgeCellRenderer = (value: any): React.ReactNode => {
    return value ? (
      <Badge variant={trueVariant}>{trueLabel}</Badge>
    ) : (
      <Badge variant={falseVariant}>{falseLabel}</Badge>
    );
  };

  BooleanBadgeCellRenderer.displayName = 'BooleanBadgeCellRenderer';
  return BooleanBadgeCellRenderer;
}

/**
 * 省略可能なテキストセル
 */
export function createTruncatedTextCell(maxWidth: number = 200) {
  const TruncatedTextCellRenderer = (value: any): React.ReactNode => {
    const text = value?.toString() || '-';
    return (
      <div
        className={`max-w-[${maxWidth}px] truncate`}
        title={text}
      >
        {text}
      </div>
    );
  };

  TruncatedTextCellRenderer.displayName = 'TruncatedTextCellRenderer';
  return TruncatedTextCellRenderer;
}

/**
 * アクションメニューカラムを生成
 */
export function createActionsColumn<T extends { id: string }>(
  config?: ActionMenuConfig<T>
): ColumnDef<T> {
  return {
    id: "actions",
    enableHiding: false,
    size: 50,
    cell: ({ row }) => {
      const entity = row.original;

      if (!config) return null;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">メニューを開く</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>操作</DropdownMenuLabel>

            {config.showCopyId && (
              <>
                <DropdownMenuItem
                  onClick={() => navigator.clipboard.writeText(entity.id)}
                >
                  IDをコピー
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}

            {config.items.map((item, index) => {
              if (item.show && !item.show(entity)) return null;

              return (
                <DropdownMenuItem
                  key={index}
                  onClick={() => item.onClick(entity)}
                  className={item.variant === 'destructive' ? 'text-destructive' : ''}
                >
                  {item.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  };
}

/**
 * 汎用的なテーブルカラムを簡単に生成するヘルパー
 */
export function createGenericColumns<T extends { id: string }>(
  columns: GenericColumnConfig<T>[],
  options?: {
    showSelect?: boolean;
    actionMenu?: ActionMenuConfig<T>;
  }
): ColumnDef<T>[] {
  const result: ColumnDef<T>[] = [];

  // 選択カラム
  if (options?.showSelect) {
    result.push(createSelectColumn<T>());
  }

  // データカラム
  columns.forEach(config => {
    result.push(createDataColumn(config));
  });

  // アクションカラム
  if (options?.actionMenu) {
    result.push(createActionsColumn(options.actionMenu));
  }

  return result;
}